import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Compass, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * AI Portfolio Narrative — executive interpretation of the program
 * portfolio as navigation initiatives. No new math: it narrates the ROI /
 * conversion / condition figures the cards already show. On-demand with
 * server-side caching keyed on a portfolio signature.
 */
export function PortfolioNarrativeCard({ programROIData, orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const programs = programROIData?.programs || [];
  const ready = programs.length > 0;

  const payload = useMemo(() => ({
    ...(orgId && orgId !== 'all' ? { organization_id: orgId } : {}),
    portfolio: {
      total_programs: programROIData?.total || programs.length,
      programs_with_data: programROIData?.programs_with_data || 0,
      conversion_efficiency: programROIData?.summary?.conversion_efficiency ?? null,
      average_roi_percentage: programROIData?.summary?.average_roi_percentage ?? null,
      programs: programs.slice(0, 12).map((p) => ({
        name: p.program_name,
        roi: p.roi_multiplier,
        conversion_efficiency: p.conversion_efficiency,
        trajectory: p.trajectory,
        weakest_stage: p.weakest_stage ? { label: p.weakest_stage.label } : null,
        strongest_stage: p.strongest_stage ? { label: p.strongest_stage.label } : null,
      })),
    },
  }), [programROIData, programs, orgId]);

  const fetchNarrative = useCallback(async ({ force = false, cachedOnly = false } = {}) => {
    if (!ready) return;
    if (!cachedOnly) setLoading(true);
    try {
      const res = await api.post('/analytics/program-portfolio/narrative', { ...payload, force, cached_only: cachedOnly });
      setData(res.data?.available === false ? null : res.data);
    } catch (err) {
      if (!cachedOnly) toast.error(err.response?.data?.detail || 'Failed to generate portfolio narrative');
    }
    setLoading(false);
  }, [ready, payload]);

  useEffect(() => { setData(null); fetchNarrative({ cachedOnly: true }); }, [fetchNarrative]);

  if (!ready) return null;

  return (
    <div className="rounded-xl border border-cyan-200 bg-cyan-50/50 p-4" data-testid="portfolio-narrative-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-800 flex items-center gap-1.5">
          <Compass className="h-3.5 w-3.5" /> AI Portfolio Navigation Summary
        </p>
        {data ? (
          <Button size="sm" variant="ghost" className="gap-1 text-[11px] text-cyan-700 hover:bg-cyan-100 h-7 px-2"
            onClick={() => fetchNarrative({ force: true })} disabled={loading} data-testid="portfolio-narrative-regenerate-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        ) : (
          <Button size="sm" className="gap-1.5 bg-cyan-700 hover:bg-cyan-800 text-white h-7"
            onClick={() => fetchNarrative()} disabled={loading} data-testid="portfolio-narrative-generate-btn">
            {loading ? <><RefreshCw className="h-3 w-3 animate-spin" /> Interpreting…</> : <><Sparkles className="h-3.5 w-3.5" /> Generate</>}
          </Button>
        )}
      </div>
      {loading && !data ? (
        <p className="text-sm text-slate-500 mt-2">Interpreting which initiatives are converting capability into value…</p>
      ) : data ? (
        <>
          <p className="text-sm text-slate-700 leading-relaxed mt-2" data-testid="portfolio-narrative-text">{data.summary}</p>
          <p className="text-[10px] text-slate-400 mt-1.5">
            {data.cached ? 'Cached · ' : ''}{data.generated_at ? new Date(data.generated_at).toLocaleString() : ''} · interpretation only — no new math
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-500 mt-2">
          One-paragraph executive read of which programs are successfully navigating toward value — and where value is leaking along the journey.
        </p>
      )}
    </div>
  );
}
